/** Ordered translation engine chain with rate-limit fallback — v4.73.1 */

import { getTranslationApiKeys, getTranslationKeyStatus } from './translationRuntimeKeys';
import { foldSpokenEmails } from './sensitiveDataProtector';

const DEFAULT_AZURE_REGION = 'brazilsouth';

const BLACKBOX = {};
const BLACKBOX_TTL = 86400000; // 24h
const BLACKBOX_TTLS = {};
const BLACKBOX_REASONS = {};
const SESSION_BLACKLIST_KEY = 'catint_trans_engine_blacklist_v1';
const LOGGED_FAILS = new Set();

let lastTranslationAttempt = null;

export const getLastTranslationAttempt = () => lastTranslationAttempt;

const readSessionBlacklist = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_BLACKLIST_KEY) || '{}');
  } catch {
    return {};
  }
};

const writeSessionBlacklist = (map) => {
  try {
    sessionStorage.setItem(SESSION_BLACKLIST_KEY, JSON.stringify(map));
  } catch (_) {}
};

const sessionEntryUntil = (entry) => {
  if (!entry) return 0;
  if (typeof entry === 'number') return entry;
  return entry.until || 0;
};

export const isEngineBlocked = (id) => {
  const memTtl = BLACKBOX_TTLS[id] || BLACKBOX_TTL;
  if (BLACKBOX[id] && Date.now() - BLACKBOX[id] < memTtl) return true;
  const session = readSessionBlacklist();
  const until = sessionEntryUntil(session[id]);
  return until > Date.now();
};

const LOCAL_TRANSLATE_ENGINE = 'local_stt';
const GATEWAY_ENGINE = 'gateway';
const FREE_ENGINE_BLACKLIST_MS = 10 * 60 * 1000; // 10m — network blips
const RATE_LIMIT_BLACKLIST_MS = 30 * 60 * 1000; // 30m — don't hammer 429 APIs
const SERVER_ERROR_BLACKLIST_MS = 90 * 1000; // 90s — dead gateway, retry soon but don't hammer per segment
// v4.165.0: a hanging engine used to be retried on EVERY segment (timeouts were
// never blacklisted), so one dead endpoint cost 4s per bubble, all day — the
// reported "4-6s behind". 90s is long enough to stop the bleeding, short enough
// that a recovered engine returns on its own.
const TIMEOUT_BLACKLIST_MS = 90 * 1000;
// The local engine is on this machine and is never rate limited, so a timeout
// there means "slow", not "dead". Measured: a request queued behind the previous
// turn's decode answers 200 in ~1.8s — the old 90s block then blinded the whole
// session and every bubble went to the remote gateway (or nowhere).
const LOCAL_TIMEOUT_BLACKLIST_MS = 15 * 1000;
// Budget for the local engine: the API queues a turn for up to
// CATTS_TRANSLATE_QUEUE_WAIT_SEC (3s) behind the decode it just admitted, then
// decodes (~0.2-1.5s). The 1.2s first rung of the ladder cut that off mid-flight
// and produced a phantom "engine dead". A refused connection still fails
// instantly, so a genuinely down API costs nothing here.
const LOCAL_ENGINE_TIMEOUT_MS = 6000;

export const blacklistEngine = (id, ttlMs, reason = 'error') => {
  const rateLimited = reason === 'rate_limit';
  const serverDown = reason === 'server_error';
  const timedOut = reason === 'timeout';
  const effectiveTtl =
    ttlMs ??
    (rateLimited
      ? RATE_LIMIT_BLACKLIST_MS
      : serverDown
        ? SERVER_ERROR_BLACKLIST_MS
        : timedOut
          ? id === LOCAL_TRANSLATE_ENGINE
            ? LOCAL_TIMEOUT_BLACKLIST_MS
            : TIMEOUT_BLACKLIST_MS
          : id === LOCAL_TRANSLATE_ENGINE
            ? FREE_ENGINE_BLACKLIST_MS
            : BLACKBOX_TTL);
  BLACKBOX[id] = Date.now();
  BLACKBOX_TTLS[id] = effectiveTtl;
  BLACKBOX_REASONS[id] = reason;
  const session = readSessionBlacklist();
  session[id] = { until: Date.now() + effectiveTtl, reason };
  writeSessionBlacklist(session);
};

/** Clear only transient blocks (network blips) — keep rate_limit + server_error cooldowns. */
export const clearTransientEngineBlacklist = () => {
  const session = readSessionBlacklist();
  Object.keys(session).forEach((id) => {
    const entry = session[id];
    const reason = typeof entry === 'object' ? entry.reason : 'error';
    if (reason === 'rate_limit' || reason === 'server_error') return;
    delete session[id];
    delete BLACKBOX[id];
    delete BLACKBOX_TTLS[id];
    delete BLACKBOX_REASONS[id];
  });
  writeSessionBlacklist(session);
};

export const clearSessionEngineBlacklist = () => {
  try {
    sessionStorage.removeItem(SESSION_BLACKLIST_KEY);
  } catch (_) {}
  Object.keys(BLACKBOX).forEach((k) => delete BLACKBOX[k]);
  Object.keys(BLACKBOX_TTLS).forEach((k) => delete BLACKBOX_TTLS[k]);
  Object.keys(BLACKBOX_REASONS).forEach((k) => delete BLACKBOX_REASONS[k]);
  lastAzureOutcome = null;
};

/** Auth failures — do not treat as rate limit (403 can be key/region mismatch). */
export const isUnauthorizedError = (err, body = '') => {
  const s = `${err?.message || err || ''} ${body}`.toUpperCase();
  return (
    /\b401\b/.test(s) ||
    /\b403\b/.test(s) ||
    s.includes('UNAUTHORIZED') ||
    s.includes('AUTHENTICATION') ||
    s.includes('ACCESS DENIED')
  );
};

export const isRateLimitError = (err, body = '') => {
  if (isUnauthorizedError(err, body)) return false;
  const s = `${err?.message || err || ''} ${body}`.toUpperCase();
  return (
    s.includes('429') ||
    s.includes('LIMIT') ||
    s.includes('THROTTLED') ||
    s.includes('QUOTA') ||
    s.includes('MYMEMORY WARNING')
  );
};

export const isBrowserFetchError = (err) => {
  const s = `${err?.message || err || ''}`.toLowerCase();
  return (
    s.includes('failed to fetch') ||
    s.includes('networkerror') ||
    s.includes('network error') ||
    s.includes('load failed') ||
    s.includes('cors')
  );
};

/** Dead upstream (502 Bad Gateway, 503…) — back off instead of hammering per segment. */
export const isServerError = (err) => {
  const s = `${err?.message || err || ''}`.toLowerCase();
  return (
    /\b50\d\b/.test(s) ||
    s.includes('bad gateway') ||
    s.includes('service unavailable') ||
    s.includes('server error')
  );
};

export const classifyEngineFailure = (err) => {
  if (isUnauthorizedError(err)) return 'unauthorized';
  if (isRateLimitError(err)) return 'rate_limit';
  if (isBrowserFetchError(err)) return 'cors_or_network';
  if (isServerError(err)) return 'server_error';
  if (`${err?.message || ''}`.includes('timeout')) return 'timeout';
  return 'error';
};

/** Last Azure outcome only — never stores keys, bodies, or translated text. */
let lastAzureOutcome = null; // { status: 'ok'|'unauthorized'|'error', at: number }

export const getLastAzureOutcome = () => lastAzureOutcome;

/**
 * Visible Azure line for Settings / Phase 0.
 * "ok" only after a successful Azure request — key presence alone is never ok.
 */
export const getAzureStatusLabel = () => {
  const keys = getTranslationKeyStatus();
  if (!keys.azure) return 'Azure: missing key';
  if (isEngineBlocked('azure')) {
    const session = readSessionBlacklist();
    const entry = session.azure;
    const reason =
      (typeof entry === 'object' && entry?.reason) ||
      BLACKBOX_REASONS.azure ||
      lastAzureOutcome?.status;
    if (reason === 'unauthorized' || lastAzureOutcome?.status === 'unauthorized') {
      return 'Azure: unauthorized / key-region mismatch';
    }
    return 'Azure: paused';
  }
  if (lastAzureOutcome?.status === 'ok') return 'Azure: ok';
  if (lastAzureOutcome?.status === 'unauthorized') {
    return 'Azure: unauthorized / key-region mismatch';
  }
  if (lastAzureOutcome?.status === 'error') return 'Azure: error';
  return 'Azure: key configured (unverified)';
};

export const isAzureFallbackOnly = () => {
  const keys = getTranslationKeyStatus();
  if (!keys.azure) return false;
  if (lastAzureOutcome?.status === 'unauthorized') return true;
  if (!isEngineBlocked('azure')) return false;
  const session = readSessionBlacklist();
  const entry = session.azure;
  const reason = (typeof entry === 'object' && entry?.reason) || BLACKBOX_REASONS.azure;
  return reason === 'unauthorized' || lastAzureOutcome?.status === 'unauthorized';
};

const logEngineFailOnce = (id, reason) => {
  const key = `${id}:${reason}`;
  if (LOGGED_FAILS.has(key)) return;
  LOGGED_FAILS.add(key);
};

export const sanitizeEngineResponse = (input) => {
  if (!input || typeof input !== 'string') return '';
  const upper = input.toUpperCase();
  if (
    upper.includes('MYMEMORY') ||
    upper.includes('LIMIT') ||
    upper.includes('THROTTLED') ||
    upper.includes('FORBIDDEN') ||
    upper.includes('QUOTA') ||
    input.includes('<html>')
  ) {
    return '';
  }
  return input.trim();
};

export { getTranslationKeyStatus } from './translationRuntimeKeys';

export const getTranslationEngineHealth = () => {
  const keys = getTranslationKeyStatus();
  const apiKeys = getTranslationApiKeys();
  const chain = buildEngineChain('en', 'es', apiKeys);
  const blocked = [LOCAL_TRANSLATE_ENGINE, GATEWAY_ENGINE].filter((id) =>
    isEngineBlocked(id),
  );
  return {
    keys,
    chain,
    blocked,
    lastAttempt: lastTranslationAttempt,
    azureStatus: getAzureStatusLabel(),
    azureFallbackOnly: isAzureFallbackOnly(),
    azureRegion: apiKeys.AZURE_REGION || DEFAULT_AZURE_REGION,
  };
};

/**
 * Browser boundary: translation traffic may only reach our own gateway.
 * Never add a provider URL here: it leaks keys, produces CORS noise, and
 * makes every caption compete with a provider from the call tab.
 */
const buildFetchers = (sLang, tLang, keys, signal) => ({
  [LOCAL_TRANSLATE_ENGINE]: async (text) => {
    const r = await fetch('http://127.0.0.1:59200/stt/translate', {
      method: 'POST', signal, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, from_lang: sLang, to_lang: tLang }),
    });
    if (!r.ok) throw new Error(`local translator ${r.status}`);
    return (await r.json())?.text || '';
  },
  [GATEWAY_ENGINE]: async (text) => {
    const r = await fetch('/api/translate', {
      method: 'POST', signal, headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, sourceLang: sLang, targetLang: tLang }),
    });
    if (!r.ok) throw new Error(`gateway ${r.status}`);
    return (await r.json())?.translation || '';
  },
});

/** Browser-safe chain — lingva removed (CORS blocked on custom domains). */
export const buildEngineChain = (sLang, tLang, keys, { forceFree = false } = {}) => {
  // Local model first; server fallback second. Neither browser request calls a
  // third-party provider, so Azure/Google cannot create DevTools noise.
  const candidates = forceFree
    ? [LOCAL_TRANSLATE_ENGINE]
    : [LOCAL_TRANSLATE_ENGINE, GATEWAY_ENGINE];
  return candidates.filter((id) => !isEngineBlocked(id));
};

const FAILED = { text: '', engineId: null, quality: 'failed', failures: [], tried: [] };

/**
 * v4.165.0 — the latency ladder.
 *
 * The chain is serial, so every engine that does not answer costs you its FULL
 * timeout before the next one is tried. With a 4s timeout that meant a single dead
 * endpoint put the translation 4-6s behind — unusable while interpreting.
 *
 * Real engines answer in 200-900ms, so a first attempt that has not answered by
 * 1200ms is not going to win, and waiting longer only makes the bubble later.
 * The LAST engine keeps the full budget, so quality is never traded away for
 * speed: we only stop waiting on engines that were already going to blow it.
 */
export const ENGINE_TIMEOUT_LADDER_MS = [1200, 2000, 4000];

/** Timeout for attempt `index` of `total` — first is snappy, last is patient. */
export const engineTimeoutForAttempt = (index, total, ladder = ENGINE_TIMEOUT_LADDER_MS) => {
  const steps = ladder?.length ? ladder : ENGINE_TIMEOUT_LADDER_MS;
  if (index >= total - 1) return steps[steps.length - 1];
  return steps[Math.min(index, steps.length - 1)];
};

export const translateWithFallback = async ({
  text,
  sLang,
  tLang,
  keys,
  signal,
  acceptFn,
  onEngineFail,
  timeoutMs = 4000,
  ladder = null, // v4.167.0: operator-picked ladder (FAST/BAL/PATIENT)
}) => {
  // v4.202.0: the engine sees "juan.r56@gmail.com", never "juan punto r 56
  // arroba…" (it translated punto/arroba into "point"/"at").
  text = foldSpokenEmails(text);
  const fetchers = buildFetchers(sLang, tLang, keys, signal);
  let chain = buildEngineChain(sLang, tLang, keys);
  // All engines paused — retry only transient (non-429) blocks on free tier.
  if (chain.length === 0) {
    clearTransientEngineBlacklist();
    chain = buildEngineChain(sLang, tLang, keys, { forceFree: true });
  }
  const failures = [];
  const tried = [];
  let lastWeak = null;
  let lastAnyClean = null;

  for (let i = 0; i < chain.length; i += 1) {
    const id = chain[i];
    const isLast = i === chain.length - 1;
    if (signal?.aborted) {
      lastTranslationAttempt = { tried, failures, engineId: null, quality: 'failed' };
      return FAILED;
    }
    tried.push(id);
    try {
      // v4.165.0: shrinking per-attempt timeout instead of one flat 4s for all.
      // v4.167.0: the operator's chosen ladder wins when provided.
      // The local engine gets its own budget — it queues server-side by design.
      const attemptTimeoutMs = id === LOCAL_TRANSLATE_ENGINE
        ? LOCAL_ENGINE_TIMEOUT_MS
        : ladder
          ? engineTimeoutForAttempt(i, chain.length, ladder)
          : engineTimeoutForAttempt(i, chain.length, [timeoutMs, timeoutMs, timeoutMs]);
      const timer = new Promise((_, rej) =>
        setTimeout(() => rej(new Error('timeout')), attemptTimeoutMs),
      );
      const raw = await Promise.race([fetchers[id](text), timer]);
      const clean = sanitizeEngineResponse(String(raw || ''));
      if (!clean) continue;
      lastAnyClean = { text: clean, engineId: id };

      const accepted = acceptFn ? acceptFn(text, clean, sLang, tLang) : clean;
      if (accepted) {
        const result = { text: accepted, engineId: id, quality: 'ok', failures, tried };
        lastTranslationAttempt = result;
        return result;
      }

      // Poor translation beats none — last engine keeps whatever it returned.
      if (isLast && clean.length >= 1) {
        lastWeak = { text: clean, engineId: id, quality: 'weak', failures, tried };
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        lastTranslationAttempt = { tried, failures, engineId: null, quality: 'failed' };
        return FAILED;
      }
      const reason = classifyEngineFailure(e);
      failures.push({ id, reason });
      logEngineFailOnce(id, reason);
      onEngineFail?.(id, reason);
      if (
        isRateLimitError(e) ||
        isBrowserFetchError(e) ||
        reason === 'unauthorized' ||
        reason === 'server_error' ||
        // v4.165.0: a timeout MUST be remembered. Without this the same hanging
        // engine was retried on every single segment, so the app paid the full
        // timeout again for every bubble, all day. That was the 4-6s lag.
        reason === 'timeout'
      ) {
        blacklistEngine(id, undefined, reason);
      }
    }
  }

  if (lastWeak) {
    lastTranslationAttempt = lastWeak;
    return lastWeak;
  }

  if (lastAnyClean?.text) {
    const fallback = {
      text: lastAnyClean.text,
      engineId: lastAnyClean.engineId,
      quality: 'weak',
      failures,
      tried,
    };
    lastTranslationAttempt = fallback;
    return fallback;
  }

  const result = { ...FAILED, failures, tried };
  lastTranslationAttempt = result;
  return result;
};
